/**
 * ログ出力共通ライブラリ
 * 統一ログ出力、ログフォーマット
 * ブラウザ環境およびNode.js環境で使用可能
 */

/**
 * ログレベルの優先順位（数値が大きいほど重要度が高い）
 */
const LOG_LEVEL_PRIORITY = {
  'DEBUG': 1,
  'INFO': 2,
  'WARN': 3,
  'ERROR': 4,
  'CRITICAL': 5
};

/**
 * 現在のログレベルを取得
 * @returns {string} ログレベル（デフォルト: INFO）
 */
function getCurrentLogLevel() {
  // ブラウザ環境
  if (typeof window !== 'undefined') {
    // 環境変数またはグローバル変数から取得
    const logLevel = (window.LOG_LEVEL || 'INFO').toUpperCase();
    if (LOG_LEVEL_PRIORITY[logLevel]) {
      return logLevel;
    }
    return 'INFO';
  }
  
  // Node.js環境
  if (typeof process !== 'undefined' && process.env) {
    const logLevel = (process.env.LOG_LEVEL || 'INFO').toUpperCase();
    if (LOG_LEVEL_PRIORITY[logLevel]) {
      return logLevel;
    }
    return 'INFO';
  }
  
  return 'INFO';
}

/**
 * ログレベルが出力対象かどうかを判定
 * @param {string} level - チェックするログレベル
 * @returns {boolean} 出力する場合true
 */
function shouldLog(level) {
  const currentLevel = getCurrentLogLevel();
  const currentPriority = LOG_LEVEL_PRIORITY[currentLevel] || 2;
  const messagePriority = LOG_LEVEL_PRIORITY[level] || 2;
  
  return messagePriority >= currentPriority;
}

/**
 * 機密情報をマスキングする
 * @param {Object} obj - マスキング対象のオブジェクト
 * @returns {Object} マスキング後のオブジェクト
 */
function maskSensitiveData(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => maskSensitiveData(item));
  }
  
  const masked = {};
  // 機密情報としてマスキングするフィールド
  const sensitiveFields = [
    'password', 'token', 'accessToken', 'refreshToken', 'apiKey', 'secret',
    'secretKey', 'clientSecret', 'authorization', 'authToken',
    'email', 'mail', 'phone', 'tel', 'address', 'postalCode',
    'creditCard', 'ssn', 'sessionId', 'sessionToken', 'cookie'
  ];
  // マスキングしないフィールド（設計上ログに出力が必要な情報）
  const allowedFields = [
    'clientId', 'client_id', 'redirectUri', 'redirect_uri', 'user_id', 'message_id',
    'token_refreshed', 'originalUserId'
  ];
  
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const lowerKey = key.toLowerCase();
      // 許可されたフィールドはマスキングしない
      const isAllowed = allowedFields.some(field => lowerKey === field.toLowerCase());
      if (isAllowed) {
        masked[key] = obj[key];
      } else {
        // 機密情報フィールドをチェック
        const isSensitive = sensitiveFields.some(field => lowerKey.includes(field.toLowerCase()));
        
        if (isSensitive) {
          masked[key] = '***MASKED***';
        } else if (typeof obj[key] === 'object') {
          masked[key] = maskSensitiveData(obj[key]);
        } else {
          masked[key] = obj[key];
        }
      }
    }
  }
  
  return masked;
}

/**
 * 長いメッセージを切り詰める
 * @param {string} message - メッセージ
 * @returns {string} 切り詰め後のメッセージ
 */
function truncateMessage(message) {
  if (typeof message !== 'string') {
    return message;
  }
  
  const maxLength = 1000;
  if (message.length <= maxLength) {
    return message;
  }
  
  return message.substring(0, maxLength) + '... (truncated)';
}

/**
 * ログエントリをフォーマット
 * @param {string} level - ログレベル
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 * @returns {Object} フォーマットされたログエントリ
 */
function formatLog(level, message, data = {}) {
  // サービス情報を取得
  const serviceName = (typeof window !== 'undefined' && window.SERVICE_NAME) || 
                      (typeof process !== 'undefined' && process.env && process.env.SERVICE_NAME) || 
                      'auth-lib';
  const env = (typeof window !== 'undefined' && window.NODE_ENV) || 
              (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) || 
              (typeof process !== 'undefined' && process.env && process.env.ENVIRONMENT) || 
              'development';
  const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || 
               (typeof process !== 'undefined' && process.env && process.env.HOSTNAME) || 
               'browser';
  
  // データをマスキング
  const maskedData = maskSensitiveData(data.data || {});
  
  // メッセージを切り詰め
  const truncatedMessage = truncateMessage(message);
  
  const logEntry = {
    event: data.event || 'auth_lib',
    category: data.category || 'auth',
    timestamp: new Date().toISOString(),
    level: level,
    service: {
      name: serviceName,
      env: env,
      version: '1.0',
      host: host
    },
    correlation: {
      request_id: data.request_id || null,
      trace_id: null,
      span_id: null
    },
    actor: {
      user_id: data.user_id || null,
      ip: data.ip || null,
      user_agent: (typeof window !== 'undefined' && window.navigator && window.navigator.userAgent) || null
    },
    http: data.http || null,
    perf: data.perf || null,
    message: truncatedMessage,
    tags: data.tags || [],
    data: maskedData
  };
  
  return logEntry;
}

/**
 * ログを出力
 * @param {string} level - ログレベル (DEBUG, INFO, WARN, ERROR, CRITICAL)
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 */
function log(level, message, data = {}) {
  // ログレベルが出力対象かどうかを判定
  if (!shouldLog(level)) {
    return;
  }
  
  // ログエントリをフォーマット
  const logEntry = formatLog(level, message, data);
  
  // ログを出力（ブラウザ環境ではconsole.log、Node.js環境ではconsole.log）
  console.log(JSON.stringify(logEntry));
  
  // エラーレベルの場合はconsole.errorも出力（開発時の可視性向上）
  if (level === 'ERROR' || level === 'CRITICAL') {
    console.error(JSON.stringify(logEntry));
  }
}

/**
 * 情報ログを出力
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 */
function info(message, data = {}) {
  log('INFO', message, data);
}

/**
 * 警告ログを出力
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 */
function warn(message, data = {}) {
  log('WARN', message, data);
}

/**
 * エラーログを出力
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 */
function error(message, data = {}) {
  log('ERROR', message, data);
}

/**
 * デバッグログを出力
 * @param {string} message - ログメッセージ
 * @param {Object} data - 追加データ
 */
function debug(message, data = {}) {
  log('DEBUG', message, data);
}

module.exports = {
  // ログ出力
  log,
  
  // ログフォーマット
  formatLog,
  
  // 情報ログ
  info,
  
  // 警告ログ
  warn,
  
  // エラーログ
  error,
  
  // デバッグログ
  debug
};
